import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronRight, ChevronDown, Calendar, Users, Layers } from 'react-feather';
import { listSegmentationRuns, listSegments, type SegmentationRunSummary } from '../Segmentation/api/client';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f9fa;
`;

const Title = styled.h2`
  font-size: 17px;
  font-weight: 600;
  color: #323232;
  margin: 0 0 6px 0;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #626262;
  margin: 0;
  line-height: 1.4;
  max-width: 400px;
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const RunCard = styled.div<{ $expanded: boolean }>`
  background: #ffffff;
  border: 2px solid ${props => props.$expanded ? props.theme.palette.sentiment.primary.main : '#e5e5e5'};
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.2s;
`;

const RunHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${props => props.theme.palette.sentiment.primary.lighter};
  }
`;

const RunIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${props => props.theme.palette.sentiment.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
`;

const RunInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RunName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RunMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #626262;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ChevronIcon = styled.div<{ $expanded: boolean }>`
  color: ${props => props.theme.palette.sentiment.primary.main};
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
`;

const RunContent = styled.div<{ $expanded: boolean }>`
  display: ${props => props.$expanded ? 'block' : 'none'};
  border-top: 1px solid #e5e5e5;
  padding: 12px 16px;
  background: #fafafa;
`;

const SegmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const SegmentItem = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${props => props.$selected ? props.theme.palette.sentiment.primary.light : '#ffffff'};
  border: 2px solid ${props => props.$selected ? props.theme.palette.sentiment.primary.main : '#e5e5e5'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  &:hover {
    border-color: ${props => props.theme.palette.sentiment.primary.main};
    background: ${props => props.$selected ? props.theme.palette.sentiment.primary.light : props.theme.palette.sentiment.primary.lighter};
  }
`;

const SegmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SegmentName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #323232;
  margin-bottom: 2px;
`;

const SegmentSize = styled.div`
  font-size: 11px;
  color: #626262;
`;

const ContinueButton = styled.button`
  width: 100%;
  background: ${props => props.theme.palette.sentiment.primary.main};
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.palette.sentiment.primary.dark};
  }
`;

const ContinueEntireRunButton = styled.button`
  width: 100%;
  background: #ffffff;
  border: 2px solid ${props => props.theme.palette.sentiment.primary.main};
  border-radius: 6px;
  padding: 10px 16px;
  color: ${props => props.theme.palette.sentiment.primary.main};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 8px;

  &:hover {
    background: ${props => props.theme.palette.sentiment.primary.lighter};
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #ADADAD;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #ADADAD;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #626262;
  margin-bottom: 8px;
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: #ADADAD;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-top: 1px solid #e5e5e5;
`;

const PaginationInfo = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #626262;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PaginationButton = styled.button`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 12px;
  color: #626262;
  font-weight: 500;

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.palette.sentiment.primary.main};
    color: ${props => props.theme.palette.sentiment.primary.main};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

interface RunSelectorPanelProps {
  onSelectRun: (segmentationSessionId: string, segmentIds: string[], runName: string) => void;
  onSelectSegment: (segmentationSessionId: string, segmentId: string, segmentName: string, runName: string) => void;
  onSelectionChange?: (hasSelection: boolean, continueHandler: (() => void) | null, selectionLabel: string | null) => void;
}

const RUNS_PER_PAGE = 5;

type SegmentData = {
  identifier: string;
  name: string;
  size?: number;
};

export function RunSelectorPanel({
  onSelectRun,
  onSelectSegment,
  onSelectionChange,
}: RunSelectorPanelProps): React.JSX.Element {
  const [runs, setRuns] = useState<SegmentationRunSummary[]>([]);
  const [totalRuns, setTotalRuns] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedSegment, setSelectedSegment] = useState<{
    runId: number;
    sessionId: string;
    segmentIdentifier: string;
    segmentName: string;
  } | null>(null);
  const [selectedRun, setSelectedRun] = useState<SegmentationRunSummary | null>(null);
  const [segmentsByRun, setSegmentsByRun] = useState<Record<string, SegmentData[]>>({});
  const [loadingSegments, setLoadingSegments] = useState<Record<string, boolean>>({});

  const runsRef = useRef(runs);
  const segmentsByRunRef = useRef(segmentsByRun);
  const selectedSegmentRef = useRef(selectedSegment);
  const selectedRunRef = useRef(selectedRun);
  const loadingSegmentsRef = useRef(loadingSegments);

  useEffect(() => { runsRef.current = runs; }, [runs]);
  useEffect(() => { segmentsByRunRef.current = segmentsByRun; }, [segmentsByRun]);
  useEffect(() => { selectedSegmentRef.current = selectedSegment; }, [selectedSegment]);
  useEffect(() => { selectedRunRef.current = selectedRun; }, [selectedRun]);
  useEffect(() => { loadingSegmentsRef.current = loadingSegments; }, [loadingSegments]);

  const loadSegmentsForRuns = useCallback(async (runsToFetch: SegmentationRunSummary[]) => {
    const runsToLoad = runsToFetch.filter(r =>
      !segmentsByRunRef.current[r.identifier] && !loadingSegmentsRef.current[r.identifier]
    );

    if (runsToLoad.length === 0) {
      return;
    }

    setLoadingSegments(prev => {
      const next = { ...prev };
      runsToLoad.forEach(r => { next[r.identifier] = true; });
      return next;
    });

    const results = await Promise.allSettled(
      runsToLoad.map(async (run) => {
        try {
          const response = await listSegments({ runId: run.runKey, view: 'list' });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const runSegments: SegmentData[] = response.segments.map((seg: any) => {
            const meta = (seg.metadata ?? {}) as Record<string, unknown>;
            const sampleData = (meta.sampleData ?? {}) as Record<string, unknown>;
            return {
              identifier: seg.identifier,
              name: seg.name,
              size: (sampleData.segmentN as number) ?? undefined,
            };
          });
          return { runIdentifier: run.identifier, segments: runSegments };
        } catch (error) {
          console.error('[RunSelectorPanel] Error fetching segments for run:', run.runKey, error);
          throw error;
        }
      })
    );

    setSegmentsByRun(prev => {
      const next = { ...prev };
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          next[result.value.runIdentifier] = result.value.segments;
        } else {
          next[runsToLoad[idx].identifier] = [];
          console.error('[RunSelectorPanel] Failed to fetch segments for run:', runsToLoad[idx].runKey, result.reason);
        }
      });
      return next;
    });

    setLoadingSegments(prev => {
      const next = { ...prev };
      runsToLoad.forEach(r => { next[r.identifier] = false; });
      return next;
    });
  }, []);

  const fetchPage = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const response = await listSegmentationRuns({
        status: 'complete',
        take: RUNS_PER_PAGE,
        skip: page * RUNS_PER_PAGE,
      });
      const filtered = response.runs.filter(
        (r) => r.sessionId && r.segmentCount && r.segmentCount > 0,
      );
      setRuns(filtered);
      setTotalRuns(response.total);
      if (filtered.length > 0) {
        await loadSegmentsForRuns(filtered);
      }
    } catch (error) {
      console.error('[RunSelectorPanel] Failed to fetch runs:', error);
    } finally {
      setLoading(false);
    }
  }, [loadSegmentsForRuns]);

  useEffect(() => {
    fetchPage(currentPage);
  }, [currentPage, fetchPage]);

  const totalPages = Math.ceil(totalRuns / RUNS_PER_PAGE);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  // Handler for continue button - uses refs to avoid dependency cycle
  const handleContinue = useCallback(() => {
    const currentSelectedSegment = selectedSegmentRef.current;
    const currentSelectedRun = selectedRunRef.current;

    if (currentSelectedSegment) {
      // Continue with selected segment
      const run = runsRef.current.find(r => r.id === currentSelectedSegment.runId);
      if (!run) return;
      const runName = run.projectTitle || `Run ${run.runKey}`;
      onSelectSegment(currentSelectedSegment.sessionId, currentSelectedSegment.segmentIdentifier, currentSelectedSegment.segmentName, runName);
    } else if (currentSelectedRun) {
      // Continue with entire run
      if (!currentSelectedRun.sessionId) return;
      const segments = segmentsByRunRef.current[currentSelectedRun.identifier] || [];
      const segmentIds = segments.map(s => s.identifier);
      const runName = currentSelectedRun.projectTitle || `Run ${currentSelectedRun.runKey}`;
      onSelectRun(currentSelectedRun.sessionId, segmentIds, runName);
    }
  }, [onSelectSegment, onSelectRun]);

  // Notify parent when selection changes - handleContinue is stable so don't include it
  useEffect(() => {
    if (!onSelectionChange) return;

    if (selectedSegment) {
      const run = runsRef.current.find(r => r.id === selectedSegment.runId);
      const runName = run ? (run.projectTitle || `Run ${run.runKey}`) : '';
      const label = `${runName} - ${selectedSegment.segmentName}`;
      onSelectionChange(true, handleContinue, label);
    } else if (selectedRun) {
      const runName = selectedRun.projectTitle || `Run ${selectedRun.runKey}`;
      const label = runName;
      onSelectionChange(true, handleContinue, label);
    } else {
      onSelectionChange(false, null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSegment, selectedRun, onSelectionChange]);

  const handleRunHeaderClick = async (run: SegmentationRunSummary) => {
    const isExpanding = expandedRunId !== run.id;
    console.log('[RunSelectorPanel] handleRunHeaderClick:', { runId: run.id, isExpanding, currentSegments: segmentsByRun[run.identifier]?.length || 0 });
    setExpandedRunId(isExpanding ? run.id : null);

    if (isExpanding) {
      // Select the entire run when expanding
      setSelectedRun(run);
      setSelectedSegment(null);
      // Lazy load segments when expanding
      await loadSegmentsForRuns([run]);
    }
  };

  const handleSegmentClick = (run: SegmentationRunSummary, segmentIdentifier: string, segmentName: string) => {
    if (!run.sessionId) return;
    // Highlight the segment, don't launch yet
    setSelectedSegment({
      runId: run.id,
      sessionId: run.sessionId,
      segmentIdentifier,
      segmentName,
    });
    setSelectedRun(null); // Clear run selection
  };

  if (loading) {
    return (
      <Root>
        <Header>
          <Title>Select Segmentation</Title>
          <Subtitle>Select a segmentation to start chatting with message generation agent</Subtitle>
        </Header>
        <ScrollContainer>
          <LoadingState>Loading segmentation runs...</LoadingState>
        </ScrollContainer>
      </Root>
    );
  }

  if (runs.length === 0) {
    return (
      <Root>
        <Header>
          <Title>Select Segmentation</Title>
          <Subtitle>Select a segmentation to start chatting with message generation agent</Subtitle>
        </Header>
        <ScrollContainer>
          <EmptyState>
            <EmptyIcon>📊</EmptyIcon>
            <EmptyTitle>No Segmentation Runs Found</EmptyTitle>
            <EmptyText>
              Complete a segmentation run first, then return here to generate marketing messages.
            </EmptyText>
          </EmptyState>
        </ScrollContainer>
      </Root>
    );
  }

  return (
    <Root>
      <Header>
        <Title>Select Segmentation Run</Title>
        <Subtitle>Choose a run or segment to generate marketing messages</Subtitle>
      </Header>
      <ScrollContainer>
        {runs.map((run) => {
          const isExpanded = expandedRunId === run.id;
          const segments = segmentsByRun[run.identifier] || [];

          return (
            <RunCard key={run.id} $expanded={isExpanded}>
              <RunHeader onClick={() => handleRunHeaderClick(run)}>
                <RunIcon>
                  <Layers size={20} />
                </RunIcon>
                <RunInfo>
                  <RunName>{run.projectTitle || `Run ${run.runKey}`}</RunName>
                  <RunMeta>
                    {run.runTimestamp && (
                      <MetaItem>
                        {new Date(run.runTimestamp).toLocaleDateString()}
                      </MetaItem>
                    )}
                    {run.segmentCount && (
                      <MetaItem>
                        {run.segmentCount} segments
                      </MetaItem>
                    )}
                    {run.totalPatientPopulation && (
                      <MetaItem>
                        {run.totalPatientPopulation.toLocaleString()} HCPs
                      </MetaItem>
                    )}
                  </RunMeta>
                </RunInfo>
                <ChevronIcon $expanded={isExpanded}>
                  <ChevronRight size={18} />
                </ChevronIcon>
              </RunHeader>

              <RunContent $expanded={isExpanded}>
                {/* Segments list - skinny cards, clickable to highlight */}
                {segments.length > 0 && (
                  <SegmentsList>
                    {segments.map((segment) => {
                      const isSelected = selectedSegment?.runId === run.id && selectedSegment?.segmentIdentifier === segment.identifier;
                      return (
                        <SegmentItem
                          key={segment.identifier}
                          $selected={isSelected}
                          onClick={() => handleSegmentClick(run, segment.identifier, segment.name)}
                        >
                          <SegmentInfo>
                            <SegmentName>{segment.name}</SegmentName>
                            {segment.size && (
                              <SegmentSize>{segment.size.toLocaleString()} HCPs</SegmentSize>
                            )}
                          </SegmentInfo>
                        </SegmentItem>
                      );
                    })}
                  </SegmentsList>
                )}
              </RunContent>
            </RunCard>
          );
        })}
      </ScrollContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Page {currentPage + 1} of {totalPages}
          </PaginationInfo>
          <PaginationButtons>
            <PaginationButton
              onClick={() => {
                setCurrentPage(prev => prev - 1);
                setExpandedRunId(null);
                setSelectedRun(null);
                setSelectedSegment(null);
              }}
              disabled={!hasPrevPage || loading}
            >
              ← Prev
            </PaginationButton>
            <PaginationButton
              onClick={() => {
                setCurrentPage(prev => prev + 1);
                setExpandedRunId(null);
                setSelectedRun(null);
                setSelectedSegment(null);
              }}
              disabled={!hasNextPage || loading}
            >
              Next →
            </PaginationButton>
          </PaginationButtons>
        </PaginationContainer>
      )}
    </Root>
  );
}
